// ============================================================
// B327 (AI_147) — DELTA "o que mudou desde a última análise" (Caminho A)
//
// Deriva, de forma DETERMINÍSTICA, o que é NOVO/MUDOU desde a análise anterior:
// exames/marcadores/consultas com data posterior à referência + mudanças de valor
// (antes → depois) nos labs e marcadores. Puro (sem React, sem I/O, sem IA) →
// testável. NÃO toca a geração — só transparência de seguimento (risco zero).
// A camada de INTERPRETAÇÃO por IA é fase 2 (B327-B).
// ============================================================

import {
  ExameLaboratorialAnonimizado,
  MarcadorAnonimizado,
  ConsultaAnonimizada,
} from "../types";
import { parseData, fmtData, ROTULOS_MARCADORES } from "./proveniencia";

export interface MudancaValor {
  rotulo: string;
  de: string;
  para: string;
}

export interface ConsultaNova {
  data: string | null;
  resumo: string;
}

export interface DeltaAnalise {
  dataReferencia: string | null; // DD/MM/AAAA da análise anterior
  novosExamesLab: number;
  novosMarcadores: number;
  consultasNovas: ConsultaNova[];
  mudancasLab: MudancaValor[];
  mudancasMarcadores: MudancaValor[];
  vazio: boolean; // nada novo nem mudado
}

export interface DadosDelta {
  examesLab?: ExameLaboratorialAnonimizado[];
  marcadores?: MarcadorAnonimizado[];
  consultas?: ConsultaAnonimizada[];
}

function ms(d?: string | null): number {
  const dt = parseData(d);
  return dt ? dt.getTime() : NaN;
}

function temValor(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s !== "" && s.toLowerCase() !== "n/a" && s.toLowerCase() !== "undefined";
}

// Resumo textual de uma consulta nova para o painel/IA. Cai no primeiro campo
// preenchido; só usa o genérico "consulta registrada" quando NENHUM texto existe
// (B327 polido, AI_148: antes só olhava queixa/conduta/hipótese → caía cedo demais
// no genérico; agora inclui HMA e exame físico). Texto INTEGRAL — a truncagem é só
// na exibição (CSS line-clamp no PainelDelta), preservando o contexto p/ a IA.
function resumoConsulta(c: ConsultaAnonimizada): string {
  return (
    (c.queixaPrincipal && c.queixaPrincipal.trim()) ||
    (c.conduta && c.conduta.trim()) ||
    (c.hipoteses && c.hipoteses.trim()) ||
    (c.hma && c.hma.trim()) ||
    (c.exameFisico && c.exameFisico.trim()) ||
    "consulta registrada"
  );
}

// O registro mais recente ESTRITAMENTE anterior/igual à referência (o "antes").
function maisRecenteAte<T>(
  itens: T[],
  getData: (t: T) => string | undefined,
  refMs: number
): T | null {
  let melhor: T | null = null;
  let melhorMs = -Infinity;
  for (const it of itens) {
    const m = ms(getData(it));
    if (!isNaN(m) && m <= refMs && m > melhorMs) {
      melhor = it;
      melhorMs = m;
    }
  }
  return melhor;
}

// O registro mais recente no geral (o "depois"/atual).
function maisRecente<T>(itens: T[], getData: (t: T) => string | undefined): T | null {
  let melhor: T | null = null;
  let melhorMs = -Infinity;
  for (const it of itens) {
    const m = ms(getData(it));
    const val = isNaN(m) ? -Infinity : m;
    if (val > melhorMs) {
      melhor = it;
      melhorMs = val;
    }
  }
  return melhor;
}

// Dois valores são "o mesmo" se forem strings idênticas OU numericamente iguais
// (tolera vírgula×ponto decimal: "0,75" e "0.75" NÃO são mudança — evita o falso
// positivo visto ao vivo). Valores não-numéricos (ex.: "Reagente") comparam como texto.
function mesmoValor(a: unknown, b: unknown): boolean {
  const sa = String(a).trim();
  const sb = String(b).trim();
  if (sa === sb) return true;
  if (sa.replace(",", ".") === sb.replace(",", ".")) return true;
  const na = Number(sa.replace(",", "."));
  const nb = Number(sb.replace(",", "."));
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

// Compara dois mapas de valores (labs) chave a chave; retorna as diferenças.
function diffValores(
  de: Record<string, unknown>,
  para: Record<string, unknown>,
  rotuloDe: (k: string) => string
): MudancaValor[] {
  const mud: MudancaValor[] = [];
  for (const [k, vPara] of Object.entries(para)) {
    if (!temValor(vPara)) continue;
    const vDe = de[k];
    if (!temValor(vDe)) continue; // valor novo (não é "mudança", conta como novo exame)
    if (!mesmoValor(vDe, vPara)) {
      mud.push({ rotulo: rotuloDe(k), de: String(vDe), para: String(vPara) });
    }
  }
  return mud;
}

/**
 * Deriva o delta desde `dataReferenciaISO` (a data da análise anterior).
 * `vazio: true` quando não há nada novo nem mudado (o painel então não aparece).
 */
export function derivarDelta(
  dados: DadosDelta,
  dataReferenciaISO: string
): DeltaAnalise {
  const refMs = ms(dataReferenciaISO);
  const examesLab = Array.isArray(dados.examesLab) ? dados.examesLab : [];
  const marcadores = Array.isArray(dados.marcadores) ? dados.marcadores : [];
  const consultas = Array.isArray(dados.consultas) ? dados.consultas : [];

  const posterior = (d?: string | null) => {
    const m = ms(d);
    return !isNaN(m) && !isNaN(refMs) && m > refMs;
  };

  // Novos desde a referência
  const novosExamesLab = examesLab.filter((e) => posterior(e.data)).length;
  const novosMarcadores = marcadores.filter((m) =>
    posterior(m.dataColeta || m.data)
  ).length;
  const consultasNovas: ConsultaNova[] = consultas
    .filter((c) => posterior(c.data))
    .sort((a, b) => ms(b.data) - ms(a.data))
    .map((c) => ({
      data: fmtData(c.data),
      resumo: resumoConsulta(c),
    }));

  // Mudanças de valor: exame/coleta mais recente ATUAL vs o mais recente ATÉ a
  // referência. Só computa se houver um "depois" mais novo que a referência.
  let mudancasLab: MudancaValor[] = [];
  const labDepois = maisRecente(examesLab, (e) => e.data);
  if (labDepois && posterior(labDepois.data)) {
    const labAntes = maisRecenteAte(examesLab, (e) => e.data, refMs);
    if (labAntes && labAntes.valores && labDepois.valores) {
      mudancasLab = diffValores(
        labAntes.valores,
        labDepois.valores,
        (k) => k
      );
    }
  }

  let mudancasMarcadores: MudancaValor[] = [];
  const marcDepois = maisRecente(marcadores, (m) => m.dataColeta || m.data);
  if (marcDepois && posterior(marcDepois.dataColeta || marcDepois.data)) {
    const marcAntes = maisRecenteAte(
      marcadores,
      (m) => m.dataColeta || m.data,
      refMs
    );
    if (marcAntes) {
      const soConhecidos = (m: MarcadorAnonimizado) => {
        const o: Record<string, unknown> = {};
        for (const k of Object.keys(ROTULOS_MARCADORES)) {
          const v = (m as Record<string, unknown>)[k];
          if (temValor(v)) o[k] = v;
        }
        return o;
      };
      mudancasMarcadores = diffValores(
        soConhecidos(marcAntes),
        soConhecidos(marcDepois),
        (k) => ROTULOS_MARCADORES[k] || k
      );
    }
  }

  const vazio =
    novosExamesLab === 0 &&
    novosMarcadores === 0 &&
    consultasNovas.length === 0 &&
    mudancasLab.length === 0 &&
    mudancasMarcadores.length === 0;

  return {
    dataReferencia: fmtData(dataReferenciaISO),
    novosExamesLab,
    novosMarcadores,
    consultasNovas,
    mudancasLab,
    mudancasMarcadores,
    vazio,
  };
}

/**
 * Formata o delta como TEXTO (fatos exatos) para a interpretação por IA (B327-B).
 * A IA recebe estes números como verdade apurada — interpreta, não recalcula.
 */
export function formatarDeltaTexto(delta: DeltaAnalise): string {
  const linhas: string[] = [];
  if (delta.dataReferencia)
    linhas.push(`Comparado à análise de ${delta.dataReferencia}.`);

  const mudancas = [...delta.mudancasLab, ...delta.mudancasMarcadores];
  if (mudancas.length > 0) {
    linhas.push("", "Mudanças de valor:");
    mudancas.forEach((m) => linhas.push(`- ${m.rotulo}: ${m.de} → ${m.para}`));
  }

  if (delta.consultasNovas.length > 0) {
    linhas.push("", "Novas consultas:");
    delta.consultasNovas.forEach((c) =>
      linhas.push(`- ${c.data ? `${c.data} — ` : ""}${c.resumo}`)
    );
  }

  const novos: string[] = [];
  if (delta.novosExamesLab > 0)
    novos.push(`${delta.novosExamesLab} novo(s) exame(s) de laboratório`);
  if (delta.novosMarcadores > 0)
    novos.push(`${delta.novosMarcadores} nova(s) coleta(s) de marcadores`);
  if (novos.length > 0) linhas.push("", `Novos dados: ${novos.join(" · ")}`);

  return linhas.join("\n").trim();
}
